// Datas do domínio: string ISO "AAAA-MM-DD", sem fuso.

export type DataISO = string;

const ISO = /^\d{4}-\d{2}-\d{2}$/;

export function isDataISO(v: unknown): v is DataISO {
  return typeof v === "string" && ISO.test(v);
}

/** Um `Date` local como "AAAA-MM-DD" (usa o fuso local, não UTC). */
export function paraISO(d: Date): DataISO {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Hoje no fuso local, como "AAAA-MM-DD". */
export function hojeISO(): DataISO {
  return paraISO(new Date());
}

/** Agora no fuso local, como "AAAA-MM-DDTHH:MM:SS" — mesmo relógio de `hojeISO`. */
export function agoraISO(d: Date = new Date()): string {
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${paraISO(d)}T${horaDe(d)}:${ss}`;
}

/** "HH:MM" de um `Date` local. */
export function horaDe(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Dia da semana de uma data ISO (0 = domingo … 6 = sábado). `NaN` se o formato
 * ou o calendário forem inválidos (ex.: "2026-02-30"), como `paraDataISOBr`.
 */
export function diaSemanaISO(iso: DataISO): number {
  if (!isDataISO(iso)) return NaN;
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y!, m! - 1, d!);
  if (dt.getFullYear() !== y || dt.getMonth() !== m! - 1 || dt.getDate() !== d) {
    return NaN;
  }
  return dt.getDay();
}

/** `iso` mais `dias` dias corridos (negativo subtrai). Vira mês e ano sozinho. */
export function somarDias(iso: DataISO, dias: number): DataISO {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y!, m! - 1, d!);
  dt.setDate(dt.getDate() + dias);
  return paraISO(dt);
}

/**
 * Semana ISO-8601 de uma data, como "AAAA-Wnn" — semana começa na segunda, e o
 * ano da semana é o da quinta-feira (não o do dia em si): 29–31/dez podem cair
 * na semana 1 do ano seguinte, e 1º/jan pode cair na última semana do ano
 * anterior. Usado para agrupar o indicador do Dashboard por semana (ADR-0016).
 */
export function semanaISO(iso: DataISO): string {
  const [y, m, d] = iso.split("-").map(Number);
  const data = new Date(y!, m! - 1, d!);
  const diaSemanaSegSeg0 = (data.getDay() + 6) % 7; // segunda=0 … domingo=6
  data.setDate(data.getDate() - diaSemanaSegSeg0 + 3); // quinta-feira desta semana
  const anoDaSemana = data.getFullYear();
  const jan4 = new Date(anoDaSemana, 0, 4); // 4/jan sempre está na semana 1
  const jan4DiaSegSeg0 = (jan4.getDay() + 6) % 7;
  const semana1Segunda = new Date(jan4);
  semana1Segunda.setDate(jan4.getDate() - jan4DiaSegSeg0);
  const diffDias = Math.round((data.getTime() - semana1Segunda.getTime()) / 86_400_000);
  const semana = Math.floor(diffDias / 7) + 1;
  return `${anoDaSemana}-W${String(semana).padStart(2, "0")}`;
}

/**
 * Data de um fato (coleta, devolução) além do limite: depois de amanhã. Só a
 * data conta, nunca a hora; até hoje + 1 passa (decisão do dono, 2026-10-01).
 */
export function depoisDeAmanha(data: DataISO, hoje: DataISO): boolean {
  return data > somarDias(hoje, 1);
}

/** "2026-09-01" → "01/09/2026". */
export function formatData(iso: DataISO): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const BR = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

/**
 * "01/09/2026" → "2026-09-01"; `null` se o formato ou a data forem inválidos
 * (ex.: 31/02) — valida o calendário de verdade, não só o formato.
 */
export function paraDataISOBr(s: string): DataISO | null {
  const m = BR.exec(s.trim());
  if (!m) return null;
  const dia = Number(m[1]);
  const mes = Number(m[2]);
  const ano = Number(m[3]);
  const d = new Date(ano, mes - 1, dia);
  if (d.getFullYear() !== ano || d.getMonth() !== mes - 1 || d.getDate() !== dia) {
    return null;
  }
  return paraISO(d);
}
